import { EventCollectorProps } from '../../../hooks/eventCollector/types';
import { IconElement } from 'types/IconElement';
import ButtonEvent from '../../../hooks/eventCollector/events/ButtonEvent';
import classes from 'classnames';
import LoadingLottieIcon from './loading-lottie-animation.json';
import Lottie from 'react-lottie-light';
import React, { memo, PropsWithChildren, useCallback } from 'react';
import styles from './Button.module.scss';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';

export type ButtonProps = {
  dataTest?: string;
  className?: string;
  outlined?: boolean;
  small?: boolean;
  fill?: boolean;
  leftIcon?: IconElement;
  rightIcon?: IconElement;
  type?: 'submit' | 'reset' | 'button';
  loading?: boolean;
  disabled?: boolean;
  name?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export default memo(function Button({
  onClick,
  disabled,
  loading,
  dataTest,
  className,
  outlined,
  small,
  fill,
  leftIcon,
  rightIcon,
  children,
  type = 'button',
  name = type === 'submit' ? '_submit' : undefined,
  eventCollector,
}: PropsWithChildren<ButtonProps> & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new ButtonEvent({
          description:
            eventCollector?.description ?? children?.toString() ?? '',
          content: children?.toString() ?? '',
        }),
      );
    },
    [
      eventCollector?.description,
      eventCollector?.dontTrack,
      children,
      onClick,
      track,
    ],
  );

  const variant = outlined
    ? styles['variant-outlined']
    : styles['variant-primary'];
  return (
    <button
      className={classes(
        styles['button-base'],
        small ? styles['size-small'] : styles['size-normal'],
        fill ? styles['full-width'] : undefined,
        variant,
        className,
      )}
      data-test={dataTest}
      onClick={handleClick}
      name={name}
      disabled={disabled || loading}
      type={type}
    >
      <>
        {leftIcon && <span className={styles.icon}>{leftIcon}</span>}
        {children && <span>{children}</span>}
        {rightIcon && <span className={styles.icon}>{rightIcon}</span>}
        {loading && (
          <div className={styles.loading}>
            <Lottie
              options={{
                animationData: LoadingLottieIcon,
              }}
            />
          </div>
        )}
      </>
    </button>
  );
});
